import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { CreateGameRequest } from '../../types/game.types';
import { Plus, Trash2 } from 'lucide-react';

interface GameFormProps {
  onSubmit: (data: CreateGameRequest) => Promise<void>;
  isLoading?: boolean;
  initialData?: Partial<CreateGameRequest>;
}

export const GameForm: React.FC<GameFormProps> = ({ onSubmit, isLoading, initialData }) => {
  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<CreateGameRequest>({
    defaultValues: initialData || {
      bundleId: '',
      name: '',
      settings: {
        defaultCurrency: 'USD',
        allowedCurrencies: ['USD', 'EUR'],
        subscriptionPlans: {
          weekly: {
            amount: 499,
            currency: 'USD',
            interval: 'week',
            intervalCount: 1,
            oppo: { subscribeToken: '', renewToken: '', cancelToken: '' },
            vivo: { subscribeToken: '', renewToken: '', cancelToken: '' },
          },
          monthly: {
            amount: 1499,
            currency: 'USD',
            interval: 'month',
            intervalCount: 1,
            oppo: { subscribeToken: '', renewToken: '', cancelToken: '' },
            vivo: { subscribeToken: '', renewToken: '', cancelToken: '' },
          },
          yearly: {
            amount: 9999,
            currency: 'USD',
            interval: 'year',
            intervalCount: 1,
            oppo: { subscribeToken: '', renewToken: '', cancelToken: '' },
            vivo: { subscribeToken: '', renewToken: '', cancelToken: '' },
          },
          lifetime: {
            amount: 2499,
            currency: 'USD',
            oppo: { adjustToken: '' },
            vivo: { adjustToken: '' },
          },
        },
        purchaseBundles: {
          starter_pack: { amount: 299, currency: 'USD', oppo: { adjustToken: '' }, vivo: { adjustToken: '' } },
          mega_pack: { amount: 1999, currency: 'USD', oppo: { adjustToken: '' }, vivo: { adjustToken: '' } },
        },
        rateLimits: { requests: 100, window: 900000 },
      },
      adjustConfig: {
        appTokens: { default: 'your_game_adjust_app_token', oppo: 'your_game_adjust_app_token', vivo: 'your_game_adjust_app_token' },
        s2sAuthToken: 'your_s2s_auth_token',
        subscriptionEvents: { created: 'sub_created_token', renewed: 'sub_renewed_token', cancelled: 'sub_cancelled_token' },
        productEvents: { coins_500: 'prod_coins_500_token', starter_pack: 'prod_starter_pack_token' },
      },
    },
  });

  const [activeTab, setActiveTab] = useState<'general' | 'subscriptions' | 'bundles' | 'adjust'>('general');
  const [newBundleName, setNewBundleName] = useState('');
  const watchedBundles = watch('settings.purchaseBundles');

  const addPurchaseBundle = () => {
    if (!newBundleName.trim()) return;
    const currentBundles = watch('settings.purchaseBundles') || {};
    setValue('settings.purchaseBundles', {
      ...currentBundles,
      [newBundleName]: {
        amount: 999,
        currency: 'USD',
        oppo: { adjustToken: '' },
        vivo: { adjustToken: '' },
      },
    });
    setNewBundleName('');
  };

  const removePurchaseBundle = (bundleId: string) => {
    const currentBundles = watch('settings.purchaseBundles') || {};
    const { [bundleId]: removed, ...rest } = currentBundles;
    setValue('settings.purchaseBundles', rest);
  };

  const renderSubscriptionPlan = (planType: 'weekly' | 'monthly' | 'yearly' | 'lifetime') => {
    const isLifetime = planType === 'lifetime';
    const title = `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan`;

    return (
      <Card key={planType} title={title} className="mb-4">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name={`settings.subscriptionPlans.${planType}.amount` as const}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  label="Amount (cents)"
                  placeholder="999"
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              )}
            />
            <Controller
              name={`settings.subscriptionPlans.${planType}.currency` as const}
              control={control}
              render={({ field }) => <Input {...field} label="Currency" placeholder="USD" />}
            />
          </div>

          {!isLifetime && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name={`settings.subscriptionPlans.${planType}.interval` as const}
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interval</label>
                      <select
                        {...field}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                      </select>
                    </div>
                  )}
                />
                <Controller
                  name={`settings.subscriptionPlans.${planType}.intervalCount` as const}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      label="Interval Count"
                      placeholder="1"
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                    />
                  )}
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Platform-Specific Tokens</h4>

                <div className="mb-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">OPPO</h5>
                  <div className="grid grid-cols-3 gap-3">
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.oppo.subscribeToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Subscribe Token" placeholder="sub_token" />}
                    />
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.oppo.renewToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Renew Token" placeholder="renew_token" />}
                    />
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.oppo.cancelToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Cancel Token" placeholder="cancel_token" />}
                    />
                  </div>
                </div>

                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-2">VIVO</h5>
                  <div className="grid grid-cols-3 gap-3">
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.vivo.subscribeToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Subscribe Token" placeholder="sub_token" />}
                    />
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.vivo.renewToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Renew Token" placeholder="renew_token" />}
                    />
                    <Controller
                      name={`settings.subscriptionPlans.${planType}.vivo.cancelToken` as const}
                      control={control}
                      render={({ field }) => <Input {...field} label="Cancel Token" placeholder="cancel_token" />}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {isLifetime && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Platform Adjust Tokens (One-time Purchase)</h4>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name={`settings.subscriptionPlans.${planType}.oppo.adjustToken` as const}
                  control={control}
                  render={({ field }) => <Input {...field} label="OPPO Adjust Token" placeholder="prod_lifetime_token" />}
                />
                <Controller
                  name={`settings.subscriptionPlans.${planType}.vivo.adjustToken` as const}
                  control={control}
                  render={({ field }) => <Input {...field} label="VIVO Adjust Token" placeholder="prod_lifetime_token" />}
                />
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card title="Basic Information">
        <div className="space-y-4">
          <Controller
            name="bundleId"
            control={control}
            rules={{ required: 'Bundle ID is required' }}
            render={({ field }) => (
              <Input
                {...field}
                label="Bundle ID"
                placeholder="com.example.mygame"
                error={errors.bundleId?.message}
                required
              />
            )}
          />
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Game name is required' }}
            render={({ field }) => (
              <Input
                {...field}
                label="Game Name"
                placeholder="Bubble Tea"
                error={errors.name?.message}
                required
              />
            )}
          />
        </div>
      </Card>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['general', 'subscriptions', 'bundles', 'adjust'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'general' && (
        <Card title="Game Settings">
          <div className="space-y-4">
            <Controller
              name="settings.defaultCurrency"
              control={control}
              render={({ field }) => <Input {...field} label="Default Currency" placeholder="USD" />}
            />
            <Controller
              name="settings.allowedCurrencies"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Currencies</label>
                  <Input
                    value={Array.isArray(field.value) ? field.value.join(', ') : ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="USD, EUR"
                    helperText="Comma-separated list of currency codes"
                  />
                </div>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="settings.rateLimits.requests"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Rate Limit Requests"
                    placeholder="100"
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 100)}
                  />
                )}
              />
              <Controller
                name="settings.rateLimits.window"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Rate Limit Window (ms)"
                    placeholder="900000"
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 900000)}
                  />
                )}
              />
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'subscriptions' && (
        <div className="space-y-4">
          <Card>
            <p className="text-sm text-gray-600">
              Configure subscription plans. Weekly, Monthly, and Yearly are recurring; Lifetime is one-time.
            </p>
          </Card>
          {(['weekly', 'monthly', 'yearly', 'lifetime'] as const).map(renderSubscriptionPlan)}
        </div>
      )}

      {activeTab === 'bundles' && (
        <div className="space-y-4">

          {Object.entries(watchedBundles || {}).map(([bundleId]) => (
            <Card
              key={bundleId}
              title={bundleId}
              actions={
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removePurchaseBundle(bundleId)}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  Remove
                </Button>
              }
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name={`settings.purchaseBundles.${bundleId}.amount` as any}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        label="Amount (cents)"
                        placeholder="299"
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    )}
                  />
                  <Controller
                    name={`settings.purchaseBundles.${bundleId}.currency` as any}
                    control={control}
                    render={({ field }) => <Input {...field} label="Currency" placeholder="USD" />}
                  />
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Platform Adjust Tokens</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Controller
                      name={`settings.purchaseBundles.${bundleId}.oppo.adjustToken` as any}
                      control={control}
                      render={({ field }) => <Input {...field} label="OPPO Adjust Token" placeholder="prod_oppo_token" />}
                    />
                    <Controller
                      name={`settings.purchaseBundles.${bundleId}.vivo.adjustToken` as any}
                      control={control}
                      render={({ field }) => <Input {...field} label="VIVO Adjust Token" placeholder="prod_vivo_token" />}
                    />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'adjust' && (
        <div className="space-y-6">
          <Card title="App Tokens">
            <div className="space-y-4">
              <Controller
                name="adjustConfig.appTokens.default"
                control={control}
                render={({ field }) => <Input {...field} label="Default App Token" placeholder="your_game_adjust_app_token" />}
              />
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="adjustConfig.appTokens.oppo"
                  control={control}
                  render={({ field }) => <Input {...field} label="OPPO App Token" placeholder="oppo_adjust_app_token" />}
                />
                <Controller
                  name="adjustConfig.appTokens.vivo"
                  control={control}
                  render={({ field }) => <Input {...field} label="VIVO App Token" placeholder="vivo_adjust_app_token" />}
                />
              </div>
            </div>
          </Card>

          <Card title="Authentication">
            <Controller
              name="adjustConfig.s2sAuthToken"
              control={control}
              render={({ field }) => <Input {...field} label="S2S Auth Token" type="password" placeholder="your_s2s_auth_token" />}
            />
          </Card>

          <Card title="Subscription Events">
            <div className="space-y-4">
              <Controller
                name="adjustConfig.subscriptionEvents.created"
                control={control}
                render={({ field }) => <Input {...field} label="Created Event Token" placeholder="sub_created_token" />}
              />
              <Controller
                name="adjustConfig.subscriptionEvents.renewed"
                control={control}
                render={({ field }) => <Input {...field} label="Renewed Event Token" placeholder="sub_renewed_token" />}
              />
              <Controller
                name="adjustConfig.subscriptionEvents.cancelled"
                control={control}
                render={({ field }) => <Input {...field} label="Cancelled Event Token" placeholder="sub_cancelled_token" />}
              />
            </div>
          </Card>
{/* 
          <Card title="Product Events">
            <div className="space-y-3">
              {Object.entries(watch('adjustConfig.productEvents') || {}).map(([productId]) => (
                <div key={productId} className="grid grid-cols-2 gap-3">
                  <Input value={productId} disabled label="Product ID" />
                  <Controller
                    name={`adjustConfig.productEvents.${productId}` as any}
                    control={control}
                    render={({ field }) => <Input {...field} label="Event Token" placeholder="prod_event_token" />}
                  />
                </div>
              ))}
            </div>
          </Card> */}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="secondary" onClick={() => window.history.back()}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          Create Game
        </Button>
      </div>
    </form>
  );
};